import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import AppLayout from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Project } from "@/types";
import { Search, MapPin, Calendar, Building, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";
import { getStatusBadgeClass } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useAuthenticatedRequest } from "@/hooks/use-authenticated-request";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function ProjectsPage() {
  const { user } = useAuth();
  const authenticatedRequest = useAuthenticatedRequest();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Determine if user can create projects
  const canCreateProject = user && (
    !user.isSuperAdmin || 
    (user.isSuperAdmin && user.activeTenantContext && user.activeTenantContext !== "__ALL__" && user.activeTenantContext !== "null")
  );
  
  // Fetch projects
  const { data: projects, isLoading } = useQuery<Project[]>({
    queryKey: ["projects", user?.isSuperAdmin ? user.activeTenantContext : user?.tenantId],
    queryFn: async () => {
      const response = await authenticatedRequest("/api/projects");
      if (!response.ok) {
        throw new Error("Network response was not ok for projects");
      }
      return response.json();
    },
    enabled: !!user,
    retry: 1
  });
  
  // Filter projects based on search term and status filter
  const filteredProjects = projects?.filter(project => {
    const matchesSearch = 
      !searchTerm || 
      project.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.projectCode && project.projectCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.location && project.location.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = !statusFilter || statusFilter === "all" || project.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  return (
    <AppLayout>
      <div className="p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Portfolio</h1>
            <p className="text-muted-foreground">Manage and view all your construction projects</p>
          </div>
          {canCreateProject ? (
            <Button className="mt-4 sm:mt-0">
              <Link href="/projects/new/edit">Create Project</Link>
            </Button>
          ) : (
            <div className="mt-4 sm:mt-0">
              <Button disabled className="opacity-50 cursor-not-allowed">
                Create Project
              </Button>
            </div>
          )}
        </div>
        
        {/* Show alert for superadmin when no tenant is selected */}
        {user?.isSuperAdmin && !canCreateProject && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              To create projects, please select a specific tenant from the tenant selector in the top navigation.
            </AlertDescription>
          </Alert>
        )}
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
            <Input
              type="search"
              placeholder="Search projects..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="setup">Setup</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <CardHeader className="p-0">
                  <div className="h-3 bg-primary/10 w-full"></div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="h-6 w-3/4 mb-4 bg-muted animate-pulse rounded-md"></div>
                  <div className="space-y-3">
                    <div className="h-4 w-full bg-muted animate-pulse rounded-md"></div>
                    <div className="h-4 w-2/3 bg-muted animate-pulse rounded-md"></div>
                    <div className="h-4 w-3/4 bg-muted animate-pulse rounded-md"></div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : filteredProjects && filteredProjects.length > 0 ? (
            // Project cards
            filteredProjects.map((project) => (
              <Link key={project.projectId} href={`/projects/${project.projectId}`}>
                <Card className="overflow-hidden cursor-pointer hover:shadow-md transition-all">
                  <CardHeader className="p-0">
                    <div className={`h-3 w-full ${
                      project.status === "active" ? "bg-green-500" :
                      project.status === "setup" ? "bg-blue-500" :
                      "bg-neutral-500"
                    }`}></div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <CardTitle className="text-xl">{project.projectName}</CardTitle>
                      <Badge variant="outline" className={getStatusBadgeClass(project.status)}>
                        {project.status}
                      </Badge>
                    </div>
                    
                    {project.projectCode && (
                      <p className="text-sm text-muted-foreground mb-2">
                        Code: {project.projectCode}
                      </p>
                    )}
                    
                    <div className="space-y-2 mt-4">
                      {project.location && (
                        <div className="flex items-center text-sm">
                          <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>{project.location}</span>
                        </div>
                      )}
                      
                      {project.startDate && (
                        <div className="flex items-center text-sm">
                          <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>
                            {formatDate(project.startDate)}
                            {project.estimatedEndDate && ` - ${formatDate(project.estimatedEndDate)}`}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center text-sm">
                        <Building className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>Owner: {project.ownerOrganizationId ? project.ownerOrganizationId.substring(0, 8) : 'Not assigned'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))
          ) : (
            // No results
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground mb-4">No projects found.</p>
              {canCreateProject ? (
                <Link href="/projects/new/edit">
                  <Button>Create New Project</Button>
                </Link>
              ) : (
                <div>
                  <Button disabled className="opacity-50 cursor-not-allowed mb-2">
                    Create New Project
                  </Button>
                  {user?.isSuperAdmin && (
                    <p className="text-sm text-muted-foreground">
                      Select a tenant to create projects
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
